import { animalSpeciesHelper } from '@farmshare/utils';
import {
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
} from '@mui/material';
import { sortBy } from 'lodash';
import { useMemo } from 'react';

import { useVisibleSpecies } from 'features/wild-game';

import { EnumJobTagSpecies } from '../../lib/graphql';

/** Sentinel option. Selecting it stores `[]`, which MEANS all species. */
const ALL = '__all__';

interface SpeciesSelectProps {
  value: EnumJobTagSpecies[];
  onChange: (species: EnumJobTagSpecies[]) => void;
  label?: string;
}

/**
 * Species picker for a job tag, with an explicit "All species" option.
 *
 * "All" is stored as an EMPTY array — the absence of a restriction, so a
 * species added to the enum later applies to every all-species tag with no
 * backfill. The sentinel exists so the UI can say that out loud instead of
 * showing an empty box and hoping the reader infers it.
 *
 * Game species are filtered through `useVisibleSpecies`, so a processor
 * outside the wild-game release is never offered elk or bear here.
 * @param root0
 * @param root0.value
 * @param root0.onChange
 * @param root0.label
 */
export function SpeciesSelect({
  value,
  onChange,
  label = 'Species',
}: SpeciesSelectProps) {
  const allSpecies = useMemo(
    () => Object.values(EnumJobTagSpecies) as EnumJobTagSpecies[],
    [],
  );
  const visible = useVisibleSpecies(allSpecies);

  const options = useMemo(
    () =>
      sortBy(
        visible.map((s) => ({ ...animalSpeciesHelper(s), value: s })),
        'order',
      ),
    [visible],
  );

  const selected = value.length === 0 ? [ALL] : value;

  const handleChange = (next: string[]) => {
    // "All" and a specific species are mutually exclusive: picking All clears
    // the list, picking a species drops All.
    const addedAll = next.includes(ALL) && value.length > 0;
    if (addedAll || next.length === 0) {
      onChange([]);
      return;
    }
    onChange(next.filter((s) => s !== ALL) as EnumJobTagSpecies[]);
  };

  return (
    <FormControl size="small" sx={{ flex: 1, minWidth: 160 }}>
      <InputLabel>{label}</InputLabel>
      <Select
        multiple
        label={label}
        value={selected}
        onChange={(e) => handleChange(e.target.value as string[])}
        renderValue={(picked) =>
          (picked as string[]).includes(ALL)
            ? 'All species'
            : (picked as string[])
                .map((s) => animalSpeciesHelper(s).label)
                .join(', ')
        }
      >
        <MenuItem value={ALL}>
          <Checkbox size="small" checked={value.length === 0} />
          <ListItemText primary="All species" />
        </MenuItem>
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            <Checkbox
              size="small"
              checked={value.includes(option.value as EnumJobTagSpecies)}
            />
            <ListItemText primary={option.label} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
